import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import api from '../api/client';
import toast from 'react-hot-toast';
import {
  Clock, AlertTriangle, Activity, Heart,
  FileText, Calendar, User as UserIcon,
  ClipboardList, Tag, UserPlus, CheckCircle, Loader2,
  Video, ExternalLink, ArrowUpRight
} from 'lucide-react';
import CareJourneyTimeline from '../components/CareJourneyTimeline';

const CareEventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDoctor = user?.role === 'DOCTOR';

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [isAlreadyDoctor, setIsAlreadyDoctor] = useState(false);
  const [existingRequests, setExistingRequests] = useState([]); // Track sent requests to prevent duplicates

  useEffect(() => {
    loadEvent();
  }, [id]);

  const loadEvent = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/care-events/${id}`);
      const data = response.data.data || response.data;
      setEvent(data);
      
      // Check if current doctor is already in patient's care team
      const doctorId = user?._id || user?.id;
      if (isDoctor && data.patientId?._id && doctorId) {
        try {
          const profileRes = await api.get(`/patient-profile/${data.patientId._id}`);
          const responseData = profileRes.data?.data || {};
          const profileData = responseData.profile || responseData;
          const careTeam = profileData?.careTeam || [];
          const alreadyDoctor = careTeam.some(member => {
            const memberId = member.providerId?._id || member.providerId;
            return memberId === doctorId;
          });
          setIsAlreadyDoctor(alreadyDoctor);
          
          // Check for existing pending requests to this patient
          try {
            const requestsRes = await api.get('/care-requests/my-requests');
            setExistingRequests(requestsRes.data?.data || []);
          } catch (reqErr) {
            console.log('Could not check existing requests');
          }
        } catch (err) {
          console.log('Could not check care team status');
          setIsAlreadyDoctor(false);
        }
      }
    } catch (error) {
      toast.error('Failed to load care event');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  // Check if doctor already has a pending request to this patient
  const hasPendingRequestToPatient = () => {
    const patientId = event?.patientId?._id || event?.patientId;
    return existingRequests.some(
      req => req.patientId?._id === patientId && req.status === 'pending'
    );
  };

  const handleProcess = async () => {
    if (!isDoctor) return;
    setProcessing(true);
    // Optimistic update: show PROCESSED badge immediately
    setEvent(prev => prev ? { ...prev, status: 'processed', processedAt: new Date().toISOString() } : prev);
    try {
      await api.put(`/care-events/${id}/process`);
      toast.success('Event marked as processed');
      loadEvent();
    } catch (error) {
      // Roll back on failure
      setEvent(prev => prev ? { ...prev, status: 'active', processedAt: undefined } : prev);
      toast.error('Failed to process event');
    } finally {
      setProcessing(false);
    }
  };

  const handleRequestToJoin = async () => {
    if (!isDoctor || !event?.patientId?._id) return;
    
    setSendingRequest(true);
    try {
      await api.post('/care-requests/send', {
        patientId: event.patientId._id,
        requestedRole: 'specialist',
        message: `Dr. ${user?.name} would like to join your care team after reviewing your ${eventTypeLabels[event.eventType] || 'health report'}.`
      });
      toast.success('Care team request sent! Waiting for patient approval.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send request');
    } finally {
      setSendingRequest(false);
    }
  };

  const sevCfg = {
    critical: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-100', label: 'Critical', dot: 'bg-red-600', icon: AlertTriangle },
    high: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-100', label: 'High', dot: 'bg-orange-600', icon: Activity },
    medium: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-100', label: 'Medium', dot: 'bg-amber-500', icon: Activity },
    low: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-100', label: 'Low', dot: 'bg-emerald-500', icon: Heart },
  };

  const eventTypeLabels = {
    // Patient-initiated
    'symptom_reported':      'Symptom Reported',
    'medication_issue':      'Medication Issue',
    'appointment_missed':    'Appointment Missed',
    'service_missed':        'Service Missed',
    'patient_distress':      'Patient Distress',
    'custom_report':         'Custom Report',
    'mood_checkin':          'Mood Check-in',
    'mood_check_in':         'Mood Check-in',
    // System-detected
    'medication_missed':     'Medication Missed',
    'medication_picked_up':  'Medication Picked Up',
    'appointment_scheduled': 'Appointment Scheduled',
    'appointment_completed': 'Appointment Completed',
    'care_transition':       'Care Transition',
    'vital_threshold':       'Vital Sign Alert',
    'inactivity_detected':   'Inactivity Detected',
    // Clinical
    'doctor_response':       'Doctor Response',
    'care_plan_updated':     'Care Plan Updated',
    'risk_assessment':       'Risk Assessment',
    // Integration
    'emr_sync':              'Medical Record Sync',
    'pharmacy_sync':         'Pharmacy Sync',
    'device_reading':        'Device Reading',
    // Automated
    'caregiver_alert':       'Caregiver Alert',
    'system_signal':         'System Alert',
    // Action lifecycle
    'action_generated':      'Action Generated',
    'action_executed':       'Action Completed',
    'action_dismissed':      'Action Dismissed',
    'action_delegated':      'Action Delegated',
    'action_response':       'Action Response',
    // Consent
    'data_consent_granted':  'Data Consent Granted',
    'data_consent_revoked':  'Data Consent Revoked',
    // Care team
    'left_care_team':        'Left Care Team',
    // Medication tracking
    'medication_taken':      'Medication Taken',
  };

  const categoryLabels = {
    'general':       'General',
    'pain':          'Pain',
    'respiratory':   'Respiratory',
    'digestive':     'Digestive',
    'neurological':  'Neurological',
    'cardiac':       'Cardiac',
    'mental_health': 'Mental Health',
    'other':         'Other',
  };

  const appointmentTypeLabels = {
    'follow_up':          'Follow-up Visit',
    'routine':            'Routine Check-up',
    'urgent':             'Urgent Consultation',
    'specialist':         'Specialist Referral',
    'home_health':        'Home Health Visit',
    'virtual_visit':      'Virtual Visit',
    'home_visit':         'Home Visit',
    'pharmacist_consult': 'Pharmacist Consultation',
  };

  const timeSince = (date) => {
    const ms = Date.now() - new Date(date).getTime();
    const minutes = Math.floor(ms / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-slate-200" />
          <div className="w-48 h-4 rounded bg-slate-200" />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500">Event not found</p>
          <button onClick={() => navigate(-1)} className="mt-4 text-primary hover:underline">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const sev = sevCfg[event.severity] || sevCfg.medium;
  const SevIcon = sev.icon;
  const eventTypeLabel = eventTypeLabels[event.eventType] || event.eventType;
  const isProcessed = event.status === 'processed' || event.processedAt;

  return (
    <div className="min-h-screen bg-surface">
      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Event Card */}
        <div className="bg-white rounded-3xl shadow-premium border border-outline-variant/10 overflow-hidden relative">
          {/* Severity Accent Bar */}
          <div className="h-2 w-full" style={{ backgroundColor: sevColorMap[sev.dot] || '#00478d' }} />

          <div className="p-8">
            {/* Header Row */}
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              {/* Left: Title + Meta */}
              <div className="flex items-start gap-5 flex-1 min-w-0">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${sev.bg}`}>
                  <SevIcon className={`w-7 h-7 ${sev.text}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-xs font-extrabold px-3 py-1.5 rounded-full border ${sev.bg} ${sev.text} ${sev.border}`}>
                      <span className="flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${sev.dot}`} />
                        {sev.label.toUpperCase()}
                      </span>
                    </span>
                    <span className={`px-3 py-1.5 rounded-full text-xs font-extrabold ${
                      isProcessed ? 'bg-slate-100 text-slate-500' : 'bg-primary/10 text-primary'
                    }`}>
                      {isProcessed ? 'PROCESSED' : 'ACTIVE'}
                    </span>
                  </div>
                  <h1 className="font-headline text-3xl font-extrabold text-slate-900 tracking-tight break-words">
                    {eventTypeLabels[event.title] || event.title || eventTypeLabel}
                  </h1>
                  {isDoctor && event.patientId && (
                    <button
                      onClick={() => navigate(`/patient/${event.patientId._id || event.patientId}`)}
                      className="text-xs mt-2 flex items-center gap-1.5 font-semibold tracking-wider text-primary hover:text-primary/80 hover:underline transition-colors"
                    >
                      <UserIcon className="w-3.5 h-3.5" />
                      Patient: {event.patientId.name}
                    </button>
                  )}
                </div>
              </div>

              {/* Right: Time Stats */}
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="bg-white rounded-[1.25rem] px-5 py-4 text-center shadow-premium border border-outline-variant/10">
                  <Clock className="w-6 h-6 text-primary mx-auto mb-1" />
                  <p className="text-xs font-extrabold text-slate-500 uppercase tracking-widest">Reported</p>
                  <p className="text-sm font-bold text-slate-700">{timeSince(event.createdAt)}</p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mt-7 space-y-2">
              <div className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${sev.dot}`} />
                <p className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.2em]">Details</p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-5 border border-outline-variant/20 space-y-3">
                <p className="text-sm font-bold text-slate-900">
                  {eventTypeLabels[event.title] || event.title || eventTypeLabel}
                </p>
                {event.eventType === 'symptom_reported' && event.symptomData ? (
                  <div className="space-y-2.5">
                    {event.symptomData.description && (
                      <p className="text-[13px] text-slate-700 leading-relaxed font-medium">
                        "{event.symptomData.description}"
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-1">
                      {event.symptomData.category && (
                        <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-100 text-slate-600">
                          {categoryLabels[event.symptomData.category] || event.symptomData.category}
                        </span>
                      )}
                      {event.symptomData.severity && (
                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${sevCfg[event.symptomData.severity === 'urgent' ? 'critical' : event.symptomData.severity]?.bg} ${sevCfg[event.symptomData.severity === 'urgent' ? 'critical' : event.symptomData.severity]?.text}`}>
                          {event.symptomData.severity.charAt(0).toUpperCase() + event.symptomData.severity.slice(1)} severity
                        </span>
                      )}
                      {event.symptomData.quickCaptureType && (
                        <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-primary/10 text-primary">
                          {{
                            not_well: 'Not feeling well',
                            medication: 'Medication concern',
                            appointment: 'Appointment needed',
                            service_missed: 'Missed service',
                            confused: 'Feeling confused',
                            custom: 'Custom report',
                          }[event.symptomData.quickCaptureType] || event.symptomData.quickCaptureType}
                        </span>
                      )}
                    </div>
                    {!event.symptomData.description && !event.symptomData.severity && !event.symptomData.category && (
                      <p className="text-[13px] text-slate-400 italic">Patient reported without adding details.</p>
                    )}
                    {event.data?.symptomId && (
                      <Link
                        to={`/symptom/${event.data.symptomId}`}
                        className="inline-flex items-center gap-1.5 text-[12px] font-bold text-primary hover:text-primary/80 hover:underline transition-colors mt-1"
                      >
                        View full symptom report
                        <ArrowUpRight className="w-3.5 h-3.5" />
                      </Link>
                    )}
                  </div>
                ) : (event.eventType === 'appointment_scheduled' || event.eventType === 'appointment_completed') && event.appointmentData ? (
                  <div className="space-y-2.5">
                    {event.appointmentData.appointmentType && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600 font-medium">
                        <Calendar className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                        {appointmentTypeLabels[event.appointmentData.appointmentType] || event.appointmentData.appointmentType}
                      </div>
                    )}
                    {event.appointmentData.scheduledDate && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600 font-medium">
                        <Clock className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                        {new Date(event.appointmentData.scheduledDate).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}
                    {event.appointmentData.provider && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600 font-medium">
                        <UserIcon className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                        {event.appointmentData.provider}
                      </div>
                    )}
                    {event.appointmentData.notes && (
                      <p className="text-[13px] text-slate-500 leading-relaxed">{event.appointmentData.notes}</p>
                    )}
                    {event.appointmentData.meetingLink && (
                      <a
                        href={event.appointmentData.meetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-xs font-bold hover:bg-primary/90 transition-all"
                      >
                        <Video className="w-3.5 h-3.5" />
                        Join Virtual Visit
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                ) : (
                  <p className="text-[13px] text-slate-500 leading-relaxed font-medium">
                    {event.description || event.details || 'No additional details provided.'}
                  </p>
                )}
              </div>
            </div>

            {/* Meta Row */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              {event.category && (
                <span className="flex items-center gap-2 bg-slate-50 border border-outline-variant/20 px-4 py-2 rounded-full text-xs font-bold text-slate-600">
                  <Tag className="w-3 h-3" />
                  {categoryLabels[event.category] || event.category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </span>
              )}
              <span className="flex items-center gap-2 bg-slate-50 border border-outline-variant/20 px-4 py-2 rounded-full text-xs font-bold text-slate-600">
                <Calendar className="w-3 h-3" />
                {new Date(event.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
              <span className="flex items-center gap-2 bg-slate-50 border border-outline-variant/20 px-4 py-2 rounded-full text-xs font-bold text-slate-600">
                <FileText className="w-3 h-3" />
                {eventTypeLabel}
              </span>
            </div>

            {/* Metadata Section — rendered as human-readable key/value pairs */}
            {event.metadata && Object.keys(event.metadata).length > 0 && (
              <div className="mt-6 pt-6 border-t border-slate-100">
                <p className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.2em] mb-3">Additional Information</p>
                <div className="bg-slate-50 rounded-2xl p-5 border border-outline-variant/20 space-y-2">
                  {Object.entries(event.metadata).map(([key, value]) => {
                    const label = key
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/_/g, ' ')
                      .replace(/^\w/, c => c.toUpperCase());
                    const display = value === null || value === undefined
                      ? '—'
                      : typeof value === 'object'
                        ? JSON.stringify(value)
                        : String(value);
                    return (
                      <div key={key} className="flex items-start justify-between gap-4 text-sm">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wide shrink-0">{label}</span>
                        <span className="text-slate-800 font-medium text-right break-all">{display}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Action Footer */}
          {isDoctor && (
            <div className="px-8 pb-8 flex flex-wrap gap-3">
              {!isProcessed && (
                <button
                  onClick={handleProcess}
                  disabled={processing}
                  className="bg-primary text-white px-6 py-3 rounded-xl text-xs font-bold hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  {processing ? 'Processing…' : 'Mark as Processed'}
                </button>
              )}
              {event.patientId && !isAlreadyDoctor && !hasPendingRequestToPatient() && (
                <button
                  onClick={handleRequestToJoin}
                  disabled={sendingRequest}
                  className="bg-emerald-600 text-white px-6 py-3 rounded-xl text-xs font-bold hover:bg-emerald-700 transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  {sendingRequest ? 'Sending...' : 'Request to Join Care Team'}
                </button>
              )}
              {event.patientId && hasPendingRequestToPatient() && (
                <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span className="text-xs font-medium text-amber-700">Request Pending</span>
                </div>
              )}
              {event.patientId && isAlreadyDoctor && (
                <div className="bg-slate-100 text-slate-600 px-6 py-3 rounded-xl text-xs font-bold flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  In Care Team
                </div>
              )}
            </div>
          )}
        </div>

        {/* Care Journey — cross-linked chain */}
        <CareJourneyTimeline
          symptomId={event?.data?.symptomId}
          currentEventId={event?._id?.toString()}
        />

        {/* Timeline / Activity Section */}
        <div className="mt-8 bg-white rounded-2xl shadow-premium border border-outline-variant/10 p-7">
          <div className="flex items-center gap-3 mb-7">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary/10">
              <ClipboardList className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.2em]">Event Log</p>
              <h3 className="font-headline text-lg font-bold text-slate-900 tracking-tight">Activity Timeline</h3>
            </div>
          </div>

          <div className="relative">
            <div className="absolute left-[11px] top-3 bottom-3 w-px bg-slate-100" />
            <div className="space-y-5">
              {/* Event Created */}
              <div className="relative flex items-start gap-4 pl-8">
                <div className="absolute left-0 w-6 h-6 rounded-lg flex items-center justify-center bg-primary/10">
                  <Activity className="w-3 h-3 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800">Event Created</p>
                  <p className="text-xs text-slate-500 mt-0.5">{eventTypeLabel}</p>
                  <p className="text-xs text-slate-500 mt-1 font-semibold">
                    {new Date(event.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>

              {/* Processed (if applicable) */}
              {isProcessed && event.processedAt && (
                <div className="relative flex items-start gap-4 pl-8">
                  <div className="absolute left-0 w-6 h-6 rounded-lg flex items-center justify-center bg-emerald-100">
                    <FileText className="w-3 h-3 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-800">Event Processed</p>
                    {event.processedBy && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        By: {
                          // processedBy is stored as a plain String — either a system processor name or a doctor's ObjectId
                          /^(risk[-_]engine|action[-_]engine|risk[-_]detection[-_]engine|escalation[-_]service|system)$/i.test(event.processedBy)
                            ? 'System (Automated)'
                            : /^[a-f\d]{24}$/i.test(event.processedBy)
                              ? 'Care team member'
                              : event.processedBy
                        }
                      </p>
                    )}
                    <p className="text-xs text-slate-500 mt-1 font-semibold">
                      {new Date(event.processedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const sevColorMap = {
  'bg-red-600': '#dc2626',
  'bg-orange-600': '#ea580c',
  'bg-amber-500': '#f59e0b',
  'bg-emerald-500': '#10b981',
};

export default CareEventDetail;
